import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Heading, } from '@chakra-ui/react';
import {InstantSearch, connectSearchBox, ClearRefinements, Hits} from 'react-instantsearch-dom';
import algoliaSearch from 'algoliasearch/lite';
import PropTypes from 'prop-types';
import qs from "qs";
import {urlToSearchState} from "../../utils";
import AutoCompleteCustom from "../../AutoCompleteCustom";
import {createQuerySuggestionsPlugin} from "@algolia/autocomplete-plugin-query-suggestions";
import {createLocalStorageRecentSearchesPlugin} from "@algolia/autocomplete-plugin-recent-searches";
import {useNavigate} from "react-router-dom";
import {createRedirectUrlPlugin} from "@algolia/autocomplete-plugin-redirect-url";
import '@algolia/autocomplete-theme-classic/dist/theme.css';
import AlgoliaCustom2 from "../../AlgoliaCustom2";
import { useSelector } from "react-redux";
import AlgoliaEventManager from "../../../services/AlgoliaEventManager";

const baseAlgoliaClient = algoliaSearch(process.env.REACT_APP_ALGOLIA_APPLICATION_ID, process.env.REACT_APP_ALGOLIA_API_KEY);

// Wrapped client that adds userToken to all search requests
const algoliaClient = {
    ...baseAlgoliaClient,
    search(requests) {
        // ALWAYS set user profile before search
        AlgoliaEventManager.setAlgoliaUserProfile();
        
        const userToken = AlgoliaEventManager.getUserToken 
            ? AlgoliaEventManager.getUserToken() 
            : 'anon_user'; // Fallback
        
        const requestsWithUserToken = requests.map(request => ({
            ...request,
            params: {
                ...request.params,
                userToken,
                clickAnalytics: true,
            }
        }));
        
        return baseAlgoliaClient.search(requestsWithUserToken);
    }
};

export const INSTANT_SEARCH_INDEX_NAME = 'shopify_products';
export const INSTANT_SEARCH_QUERY_SUGGESTIONS =
    'instant_search_demo_query_suggestions';
export const INSTANT_SEARCH_HIERARCHICAL_ATTRIBUTES = [
    'hierarchicalCategories.lvl0',
    'hierarchicalCategories.lvl1',
];



const VirtualSearchBox = connectSearchBox(() => null);

function createURL(searchState) {
    return qs.stringify(searchState, { addQueryPrefix: true });
}

function searchStateToUrl({ location }, searchState) {
    // console.log(searchState);
    if (Object.keys(searchState).length === 0 || !searchState?.query) {
        return '';
    }
    return `/search${createURL(searchState)}`;
}


export const SearchInstantBox = () => {
    const [searchState, setSearchState] = useState(() =>
        urlToSearchState(window.location)
    );

    // Get user info from Redux
    const { isLoggedIn, allUserInfo } = useSelector(state => state.user);

    const navigate = useNavigate();
    const timerRef = React.useRef(null);

    // ADD THIS: Update AlgoliaEventManager when user info changes
    useEffect(() => {
        AlgoliaEventManager.setUserInfo({ isLoggedIn, allUserInfo });
        AlgoliaEventManager.setAlgoliaUserProfile();
    }, [isLoggedIn, allUserInfo?.id]);

    useEffect(() => {
        clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
            const sendTo = searchStateToUrl({ location: window.location }, searchState);
            if (sendTo.length > 0) navigate(sendTo);
        }, 400);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchState]);

    const onSubmit = useCallback(({ state }) => {
        setSearchState((searchState) => ({
            ...searchState,
            query: state.query,
        }));
    }, []);

    const onReset = useCallback(() => {
        setSearchState((searchState) => ({
            ...searchState,
            query: '',
            // hierarchicalMenu: {
            //     [INSTANT_SEARCH_HIERARCHICAL_ATTRIBUTES[0]]: '',
            // },
        }));
    }, []);

    const plugins = useMemo(() => {

        const redirectUrlPlugin = createRedirectUrlPlugin();

        const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
            key: 'search', limit: 3, transformSource({ source }) {
                return {
                    ...source, onSelect({ item }) {
                        setSearchState((searchState) => ({
                            ...searchState, query: item.label,
                        }));
                    },
                };
            },
        });

        const querySuggestionsPlugin = createQuerySuggestionsPlugin({
            //test:dispatch(testObject(searchClient)),
            searchClient: algoliaClient, indexName: 'shopify_products', getSearchParams() {
                // This creates a shared `hitsPerPage` value once the duplicates
                // between recent searches and Query Suggestions are removed.
                return recentSearchesPlugin.data.getAlgoliaSearchParams({
                    //ruleContexts: ['enable-redirect-url'],
                    hitsPerPage: 3,
                });
            },
            transformSource({ source }) {
                return {
                    ...source,
                    onSelect({ item }) {
                        if (item.objectID) {
                            // Navigate to the product page
                            navigate(`/products/${item.handle}`);
                        } else {
                            // Perform a query string search
                            navigate(`/search${createURL({ query: item.title })}`);
                        }
                        console.log(source.templates);
                        //setSearchText(item.title); // Set search text from item.title
                        // setSearchState((prevSearchState) => ({
                        //     ...prevSearchState,
                        //     query: item.title,
                        // }));
                    },
                    templates: {
                        ...source.templates,
                    }
                };
            },

        });

        return [
            querySuggestionsPlugin,
            // recentSearchesPluginugin
        ];
    }, []);

    return (
        <InstantSearch
            searchClient={algoliaClient}
            indexName={INSTANT_SEARCH_INDEX_NAME}
            searchState={searchState}
            onSearchStateChange={setSearchState}
            createURL={createURL}
        >
            <VirtualSearchBox />
            <AlgoliaCustom2
                placeholder="What are you looking for?"
                detachedMediaQuery="none"
                initialState={{
                    query: searchState.query,
                }}
                // openOnFocus={true}
                onSubmit={onSubmit}
                onReset={onReset}
                plugins={plugins}
                insights
            />
        </InstantSearch>
    )
}



